import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useContext } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MermaidDiagram } from './mermaid-diagram.js';
import { SessionCardsContext } from './slack-proposal-card.js';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { SSEEvent } from '../api/types.js';
import { DiffViewer } from './diff-viewer.js';
import { useToast } from '../hooks/use-toast.js';
import { useTokenizedLines } from '../hooks/use-shiki.js';
import { KNOWN, details, splitInjected, summary, type InjectedSegment } from '../utils/injected-blocks.js';

function EventBadge({ label, bg, fg }: { label: string; bg: string; fg: string }) {
  return (
    <span
      className="inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wide"
      style={{ backgroundColor: bg, color: fg }}
    >
      {label}
    </span>
  );
}

function CollapseToggle({ open, onClick, title, label }: { open: boolean; onClick: () => void; title: string; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="ml-auto inline-flex flex-shrink-0 items-center gap-1 font-mono text-[10px] transition-colors"
      style={{ color: 'var(--color-text-3)' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-brand-light)')}
      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-3)')}
    >
      <svg className={`h-2.5 w-2.5 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
      </svg>
      {title}
    </button>
  );
}


function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); toast('Copied to clipboard', 'success'); setTimeout(() => setCopied(false), 2000); }}
      className="absolute top-2 right-2 rounded px-1.5 py-0.5 text-[10px] font-medium opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      style={{ backgroundColor: 'var(--color-surface-3)', color: copied ? 'var(--color-success)' : 'var(--color-text-3)' }}
      aria-label="Copy to clipboard"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function CodeBlock({ content, language }: { content: string; language?: string }) {
  const tokens = useTokenizedLines(content, language);
  return (
    <div className="group relative">
      <pre className={`overflow-x-auto rounded-md p-3 text-xs ${language ? `language-${language}` : ''}`} style={{ backgroundColor: 'var(--color-surface-2)', color: 'var(--color-text)' }}>
        <code>
          {tokens
            ? tokens.map((line, i) => (
                <span key={i}>
                  {i > 0 && '\n'}
                  {line.map((t, j) => <span key={j} style={t.color ? { color: t.color } : undefined}>{t.content}</span>)}
                </span>
              ))
            : content}
        </code>
      </pre>
      <CopyButton text={content} />
    </div>
  );
}

function getLanguageFromFilePath(filePath: string): string | undefined {
  const ext = filePath.split('.').pop()?.toLowerCase();
  const map: Record<string, string> = {
    ts: 'typescript', tsx: 'typescript', js: 'javascript', jsx: 'javascript',
    py: 'python', rb: 'ruby', go: 'go', rs: 'rust',
    hcl: 'hcl', tf: 'hcl', yml: 'yaml', yaml: 'yaml',
    json: 'json', css: 'css', html: 'html', sh: 'bash',
  };
  return ext ? map[ext] : undefined;
}

function AssistantEvent({ data }: { data: Record<string, unknown> }) {
  const content = String(data.content ?? '');
  return (
    <div className="group relative prose-event text-sm" style={{ color: 'var(--color-text)' }}>
      <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</Markdown>
      <CopyButton text={content} />
    </div>
  );
}

function MarkdownCodeBlock({ className, children, node }: { className?: string; children?: React.ReactNode; node?: { position?: { start: { line: number }; end: { line: number } } } }) {
  const hasLang = className?.startsWith('language-');
  // Fenced code blocks span multiple lines in the AST; inline code stays on one line
  const isMultiline = node?.position ? node.position.end.line > node.position.start.line : false;
  const isBlock = hasLang || isMultiline || String(children ?? '').includes('\n');
  const content = String(children ?? '').replace(/\n$/, '');
  const lang = hasLang ? className!.replace('language-', '') : undefined;
  const tokens = useTokenizedLines(content, lang === 'mermaid' ? undefined : lang);

  if (isBlock && lang === 'mermaid') return <MermaidDiagram code={content} />;
  if (isBlock) {
    return (
      <div className="group relative">
        <pre className="overflow-x-auto rounded-md p-3 text-xs" style={{ backgroundColor: 'var(--color-surface-2)', color: 'var(--color-text)' }}>
          <code>
            {tokens
              ? tokens.map((line, i) => (
                  <span key={i}>
                    {i > 0 && '\n'}
                    {line.map((t, j) => <span key={j} style={t.color ? { color: t.color } : undefined}>{t.content}</span>)}
                  </span>
                ))
              : content}
          </code>
        </pre>
        <CopyButton text={content} />
      </div>
    );
  }
  // Agents often wrap a URL in backticks (device-code logins, dashboards): keep it clickable.
  if (URL_ONLY.test(content)) {
    return (
      <a href={content} target="_blank" rel="noopener noreferrer" className="rounded px-1 py-0.5 font-mono text-xs underline decoration-dotted underline-offset-2 break-all" style={{ backgroundColor: 'var(--color-surface-2)', color: 'var(--color-brand-light)' }}>
        {content}
      </a>
    );
  }
  return (
    <code className="rounded px-1 py-0.5 text-xs" style={{ backgroundColor: 'var(--color-surface-2)', color: 'var(--color-brand-light)' }}>
      {children}
    </code>
  );
}

const URL_ONLY = /^https?:\/\/\S+$/;

function MarkdownLink({ href, children }: { href?: string; children?: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2 break-all" style={{ color: 'var(--color-brand)' }}>
      {children}
    </a>
  );
}

const markdownComponents = {
  pre({ children }: { children?: React.ReactNode }) {
    // Strip <pre> wrapper — MarkdownCodeBlock renders its own <pre> for block code
    return <>{children}</>;
  },
  code: MarkdownCodeBlock,
  a: MarkdownLink,
};

function isEditTool(toolName: string): boolean {
  return ['Edit', 'str_replace', 'str_replace_editor'].includes(toolName);
}

function isWriteTool(toolName: string): boolean {
  return ['Write', 'create'].includes(toolName);
}

function EditToolEvent({ data, collapsed, meta }: { data: Record<string, unknown>; collapsed?: boolean; meta?: React.ReactNode }) {
  const toolName = String(data.tool ?? data.name ?? 'Edit');
  const input = data.input as Record<string, unknown> | undefined;
  const filePath = String(input?.file_path ?? input?.path ?? '');
  const oldStr = String(input?.old_string ?? input?.old_str ?? '');
  const newStr = String(input?.new_string ?? input?.new_str ?? '');
  const hasDiff = filePath && oldStr !== undefined && newStr !== undefined;
  const [open, setOpen] = useState(!collapsed);
  useEffect(() => { if (collapsed) setOpen(false); }, [collapsed]);

  return (
    <div>
      <div className="flex min-w-0 items-center gap-2">
        <EventBadge label={toolName} bg="var(--color-brand-dim)" fg="var(--color-brand)" />
        {filePath && <span className="min-w-0 truncate font-mono text-[11px]" style={{ color: 'var(--color-text-2)' }}>{filePath}</span>}
        {hasDiff && <CollapseToggle open={open} onClick={() => setOpen(!open)} title="Diff" />}
        {meta}
      </div>
      {hasDiff && open && (
        <div className="mt-2">
          <DiffViewer filePath={filePath} oldContent={oldStr} newContent={newStr} />
        </div>
      )}
    </div>
  );
}

function WriteToolEvent({ data, collapsed, meta }: { data: Record<string, unknown>; collapsed?: boolean; meta?: React.ReactNode }) {
  const toolName = String(data.tool ?? data.name ?? 'Write');
  const input = data.input as Record<string, unknown> | undefined;
  const filePath = String(input?.file_path ?? input?.path ?? '');
  const content = String(input?.content ?? '');
  const language = filePath ? getLanguageFromFilePath(filePath) : undefined;
  const lines = content.split('\n').length;
  const [open, setOpen] = useState(!collapsed && lines <= 30);
  useEffect(() => { if (collapsed) setOpen(false); }, [collapsed]);

  return (
    <div>
      <div className="flex min-w-0 items-center gap-2">
        <EventBadge label={toolName} bg="var(--color-brand-dim)" fg="var(--color-brand)" />
        {filePath && <span className="min-w-0 truncate font-mono text-[11px]" style={{ color: 'var(--color-text-2)' }}>{filePath}</span>}
        {content && <CollapseToggle open={open} onClick={() => setOpen(!open)} title={`${lines} lines`} />}
        {meta}
      </div>
      {content && open && (
        <div className="mt-2">
          <CodeBlock content={content} language={language} />
        </div>
      )}
    </div>
  );
}

function BashToolEvent({ data, collapsed, meta }: { data: Record<string, unknown>; collapsed?: boolean; meta?: React.ReactNode }) {
  const input = data.input as Record<string, unknown> | string | undefined;
  const command = typeof input === 'string' ? input : String((input as Record<string, unknown>)?.command ?? '');
  const lines = command.split('\n').length;
  const [open, setOpen] = useState(!collapsed);
  useEffect(() => { if (collapsed) setOpen(false); }, [collapsed]);

  return (
    <div>
      <div className="flex min-w-0 items-center gap-2">
        <EventBadge label="Bash" bg="var(--color-brand-dim)" fg="var(--color-brand)" />
        {command && <CollapseToggle open={open} onClick={() => setOpen(!open)} title={`${lines} lines`} />}
        {meta}
      </div>
      {command && open && (
        <div className="mt-2">
          <CodeBlock content={command} language="bash" />
        </div>
      )}
    </div>
  );
}

function GenericToolEvent({ data, collapsed, meta }: { data: Record<string, unknown>; collapsed?: boolean; meta?: React.ReactNode }) {
  const toolName = String(data.tool ?? data.name ?? 'Unknown tool');
  const input = data.input ?? data.arguments ?? data.params;
  const inputStr = typeof input === 'string' ? input : JSON.stringify(input, null, 2);
  const lines = inputStr?.split('\n').length ?? 0;
  const [open, setOpen] = useState(!collapsed && lines <= 20);
  useEffect(() => { if (collapsed) setOpen(false); }, [collapsed]);

  return (
    <div>
      <div className="flex min-w-0 items-center gap-2">
        <EventBadge label={toolName} bg="var(--color-brand-dim)" fg="var(--color-brand)" />
        {inputStr && <CollapseToggle open={open} onClick={() => setOpen(!open)} title={`${lines} lines`} />}
        {meta}
      </div>
      {inputStr && open && (
        <div className="mt-2">
          <CodeBlock content={inputStr} />
        </div>
      )}
    </div>
  );
}

function ToolUseEvent({ data, collapsed, meta }: { data: Record<string, unknown>; collapsed?: boolean; meta?: React.ReactNode }) {
  const toolName = String(data.tool ?? data.name ?? 'Unknown tool');

  if (isEditTool(toolName)) return <EditToolEvent data={data} collapsed={collapsed} meta={meta} />;
  if (isWriteTool(toolName)) return <WriteToolEvent data={data} collapsed={collapsed} meta={meta} />;
  if (toolName === 'Bash') return <BashToolEvent data={data} collapsed={collapsed} meta={meta} />;

  return <GenericToolEvent data={data} collapsed={collapsed} meta={meta} />;
}

function ToolResultEvent({ data, collapsed, meta }: { data: Record<string, unknown>; collapsed?: boolean; meta?: React.ReactNode }) {
  const isError = data.is_error === true || data.isError === true;
  const content = String(data.content ?? data.output ?? data.result ?? '');
  const lines = content.split('\n').length;
  const [open, setOpen] = useState(!collapsed && lines <= 20);
  useEffect(() => { if (collapsed) setOpen(false); }, [collapsed]);

  return (
    <div>
      <div className="flex min-w-0 items-center gap-2">
        <EventBadge
          label={isError ? 'Error' : 'Result'}
          bg={isError ? 'var(--color-error-dim)' : 'var(--color-success-dim)'}
          fg={isError ? 'var(--color-error)' : 'var(--color-success)'}
        />
        {content && <CollapseToggle open={open} onClick={() => setOpen(!open)} title={`${lines} lines`} />}
        {meta}
      </div>
      {content && open && (
        <div className="mt-2">
          <CodeBlock content={content} />
        </div>
      )}
    </div>
  );
}

/** Lines past which a block's details start folded */
const BLOCK_OPEN_LINES = 8;

/** A block aidev wrote into the message: its line, and what it holds under a toggle when long or only context */
function InjectedBlock({ seg }: { seg: Extract<InjectedSegment, { kind: 'block' }> }) {
  const more = details(seg);
  const line = summary(seg);
  const [open, setOpen] = useState(() => !KNOWN[seg.tag]?.background && more.split('\n').length <= BLOCK_OPEN_LINES);
  return (
    <div className="border-l-2 pl-3" style={{ borderColor: 'var(--color-border)' }} data-testid={`block-${seg.tag}`}>
      <div className="flex items-start gap-2">
        <p className="min-w-0 break-words text-sm font-medium" style={{ color: 'var(--color-text)' }}>{line}</p>
        {more && <CollapseToggle open={open} onClick={() => setOpen((o) => !o)} title={open ? 'Less' : 'More'} label={`${open ? 'Less' : 'More'}: ${KNOWN[seg.tag]?.label ?? line}`} />}
      </div>
      {open && more && <div className="mt-1 whitespace-pre-wrap break-words text-sm" style={{ color: 'var(--color-text-2)' }}>{more}</div>}
    </div>
  );
}

/** A user message's parts, read once per content: a long CI output is not parsed again on every render */
const parsed = new Map<string, InjectedSegment[]>();
function segmentsOf(content: string): InjectedSegment[] {
  let s = parsed.get(content);
  if (!s) {
    s = splitInjected(content);
    if (parsed.size > 500) parsed.clear();
    parsed.set(content, s);
  }
  return s;
}

/** A message aidev wrote into the session rather than the operator: it holds one of its blocks */
function isFromAidev(event: SSEEvent): boolean {
  const type = String(event.data.type ?? event.event);
  if (type !== 'user' && type !== 'human') return false;
  return segmentsOf(String(event.data.content ?? event.data.message ?? '')).some((s) => s.kind === 'block');
}

function UserEvent({ data }: { data: Record<string, unknown> }) {
  const content = String(data.content ?? data.message ?? '');
  const segments = segmentsOf(content);
  const injected = segments.some((s) => s.kind === 'block');
  return (
    <>
      {injected ? (
        <div className="space-y-2">
          {segments.map((s, i) => s.kind === 'block'
            ? <InjectedBlock key={i} seg={s} />
            : <div key={i} className="whitespace-pre-wrap break-words text-sm" style={{ color: 'var(--color-text-2)' }}>{s.text}</div>)}
        </div>
      ) : (
        <div className="whitespace-pre-wrap text-sm" style={{ color: 'var(--color-text)' }}>{content}</div>
      )}
      {data.waiting === true && (
        <div className="mt-1 text-xs" style={{ color: 'var(--color-text-3)' }} data-testid="prompt-waiting">
          Waiting for the session to take it
        </div>
      )}
    </>
  );
}

function QuestionEvent({ data }: { data: Record<string, unknown> }) {
  const content = String(data.content ?? data.question ?? data.message ?? '');
  const options = Array.isArray(data.options) ? (data.options as unknown[]).map(String).filter(Boolean) : [];
  // Same tones as the waiting banner: brand for a question, amber for a permission prompt.
  const isChoice = options.length > 0;
  const tone = isChoice ? 'var(--color-brand)' : 'var(--color-warn)';
  const toneDim = isChoice ? 'var(--color-brand-dim)' : 'var(--color-warn-dim)';
  return (
    <div className="rounded-md border-l-4 p-3" style={{ borderColor: tone, backgroundColor: toneDim }} data-testid="question-event" data-kind={isChoice ? 'question' : 'permission'}>
      <div className="mb-1 flex items-center gap-2">
        <EventBadge label={isChoice ? 'Question' : 'Permission'} bg={toneDim} fg={tone} />
      </div>
      {/* The choices are only offered in the waiting banner: chips here looked like buttons that do nothing. */}
      <div className="whitespace-pre-wrap text-sm" style={{ color: 'var(--color-text)' }}>{content}</div>
    </div>
  );
}

function SystemEvent({ data }: { data: Record<string, unknown> }) {
  const content = String(data.content ?? data.message ?? data.type ?? '');
  return (
    <div className="flex items-center gap-2">
      <EventBadge label="System" bg="var(--color-surface-3)" fg="var(--color-text-2)" />
      <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{content}</span>
    </div>
  );
}

function isHiddenEvent(event: SSEEvent): boolean {
  const type = String(event.data.type);
  return event.event === 'interrupted' || type === 'interrupted' || type === 'prompt_taken';
}

// A message sent while the session works (#945) waits until the session takes it, between two steps or as its next turn
export function markWaitingPrompts(events: readonly SSEEvent[]): SSEEvent[] {
  const taken = new Set(events.filter((e) => String(e.data.type) === 'prompt_taken').map((e) => String(e.data.uuid)));
  return events.map((e) => (e.event === 'output' && String(e.data.type) === 'user' && e.data.queued === true && !taken.has(String(e.data.uuid))
    ? { ...e, data: { ...e.data, waiting: true } }
    : e));
}

function isDoneEvent(event: SSEEvent): boolean {
  return event.event === 'done' || String(event.data.type) === 'done';
}

// The API persists each user prompt as { event: 'prompt', data: { type: 'human', content } }
// (the agent's SSE stream only carries its own output), so history replays show it as a user turn.
function isPersistedPrompt(event: SSEEvent): boolean {
  return event.event === 'prompt' && String(event.data.type) === 'human';
}

// The agent echoes each prompt as an output { type: 'user' } event; when that echo is present
// the persisted prompt is a duplicate and is hidden. The persisted copy is only shown for
// histories where the agent's output was never persisted (e.g. an instance replaced mid-run).
export function hideEchoedPrompts(all: readonly SSEEvent[]): SSEEvent[] {
  // The proposed next message is the prompt field's, not the history's
  const events = all.filter((e) => String(e.data.type) !== 'prompt_suggestion');
  const echoed = new Set<string>();
  for (const e of events) {
    if (e.event === 'output' && String(e.data.type) === 'user') {
      echoed.add(String(e.data.content ?? e.data.message ?? ''));
    }
  }
  if (echoed.size === 0) return [...events];
  return events.filter((e) => !(isPersistedPrompt(e) && echoed.has(String(e.data.content ?? ''))));
}

// Questions the agent asks (AskUserQuestion, permission prompts) travel as { event: 'prompt', data.type: 'question' | 'waiting' }.
function isQuestionEvent(event: SSEEvent): boolean {
  const type = String(event.data.type ?? '');
  return event.event === 'prompt' && (type === 'question' || type === 'waiting');
}

function isSystemEvent(event: SSEEvent): boolean {
  if (isPersistedPrompt(event) || isQuestionEvent(event)) return false;
  if (event.event !== 'output') return true;
  const type = String(event.data.type ?? '');
  return !['assistant', 'text', 'tool_use', 'tool_result', 'user', 'question', 'waiting'].includes(type);
}

function EventMeta({ id, timestamp }: { id: number; timestamp: string }) {
  return (
    <span className="inline-flex flex-shrink-0 items-center gap-1.5 font-mono text-[10px] opacity-40" title={formatAbsoluteTime(timestamp)}>
      <span>{formatRelativeTime(timestamp)}</span>
      <span>#{id}</span>
    </span>
  );
}

function renderEvent(event: SSEEvent, collapsed?: boolean) {
  const data = event.data;
  const type = String(data.type ?? event.event);
  const meta = <EventMeta id={event.id} timestamp={event.timestamp} />;

  switch (type) {
    case 'assistant':
    case 'text':
      return <AssistantEvent data={data} />;
    case 'tool_use':
      return <ToolUseEvent data={data} collapsed={collapsed} meta={meta} />;
    case 'tool_result':
      return <ToolResultEvent data={data} collapsed={collapsed} meta={meta} />;
    case 'user':
    case 'human':
      return <UserEvent data={data} />;
    case 'question':
    case 'waiting':
      return <QuestionEvent data={data} />;
    default:
      return <SystemEvent data={data} />;
  }
}

function getEventBorderColor(event: SSEEvent): string {
  const type = String(event.data.type ?? event.event);
  switch (type) {
    case 'assistant':
    case 'text':
      return 'var(--color-brand)';
    case 'tool_use':
      return 'var(--color-brand-dim)';
    case 'tool_result':
      return 'var(--color-success)';
    case 'user':
    case 'human':
      return 'var(--color-brand-light)';
    case 'question':
    case 'waiting':
      return Array.isArray(event.data.options) && (event.data.options as unknown[]).length > 0 ? 'var(--color-brand)' : 'var(--color-warn)';
    default:
      return 'var(--color-surface-3)';
  }
}

function formatRelativeTime(ts: string): string {
  try {
    const delta = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (delta < 0) return 'now';
    if (delta < 5) return 'now';
    if (delta < 60) return `${delta}s`;
    if (delta < 3600) return `${Math.floor(delta / 60)}m`;
    if (delta < 86400) return `${Math.floor(delta / 3600)}h`;
    return `${Math.floor(delta / 86400)}d`;
  } catch {
    return '';
  }
}

function formatAbsoluteTime(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString();
  } catch {
    return '';
  }
}

function ProcessingIndicator() {
  return (
    <div
      className="flex items-center gap-3 rounded border-l-3 px-4 py-3"
      style={{ borderLeftColor: 'var(--color-brand)', backgroundColor: 'var(--color-surface)' }}
      data-testid="processing-indicator"
    >
      <style>{`
        @keyframes cursor-blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes dot-pulse {
          0%, 20% { opacity: 0.15; }
          50% { opacity: 0.6; }
          80%, 100% { opacity: 0.15; }
        }
        .anim-cursor { animation: cursor-blink 1s step-end infinite; }
        .anim-dot { animation: dot-pulse 1.4s ease-in-out infinite; }
        .anim-dot-1 { animation-delay: 0s; }
        .anim-dot-2 { animation-delay: 0.2s; }
        .anim-dot-3 { animation-delay: 0.4s; }
      `}</style>
      <div className="flex items-center gap-1.5">
        <span
          className="anim-cursor inline-block h-4 w-[7px] rounded-[1px]"
          style={{ backgroundColor: 'var(--color-brand)' }}
        />
        <span className="flex items-center gap-[3px]">
          <span className="anim-dot anim-dot-1 inline-block h-[3px] w-[3px] rounded-full" style={{ backgroundColor: 'var(--color-text-3)' }} />
          <span className="anim-dot anim-dot-2 inline-block h-[3px] w-[3px] rounded-full" style={{ backgroundColor: 'var(--color-text-3)' }} />
          <span className="anim-dot anim-dot-3 inline-block h-[3px] w-[3px] rounded-full" style={{ backgroundColor: 'var(--color-text-3)' }} />
        </span>
      </div>
    </div>
  );
}

export interface EventTimelineProps {
  events: SSEEvent[];
  isProcessing?: boolean;
  onScrollChange?: (atBottom: boolean) => void;
  onScrolled?: (scrolledDown: boolean) => void;
}

const VIRTUALIZE_THRESHOLD = 100;

function EventRow(props: { event: SSEEvent; index: number; collapsed?: boolean }) {
  // A card the page puts after an event (a Slack message the session proposed, ADR-039 8c): never collapsed
  const card = useContext(SessionCardsContext)(props.event);
  const row = <EventRowBody {...props} />;
  return card ? <>{row}{card}</> : row;
}

function EventRowBody({ event, index, collapsed }: { event: SSEEvent; index: number; collapsed?: boolean }) {
  if (isHiddenEvent(event)) {
    return null;
  }

  if (isDoneEvent(event)) {
    return (
      <hr className="mx-4 my-1 border-t" style={{ borderColor: 'var(--color-border)' }} />
    );
  }

  if (isSystemEvent(event)) {
    return (
      <hr className="mx-4 my-1 border-t" style={{ borderColor: 'var(--color-border)' }} />
    );
  }

  const type = String(event.data.type ?? event.event);
  const isToolEvent = type === 'tool_use' || type === 'tool_result';
  // A message aidev wrote reads as the timeline's own, on the left, not as the operator's
  const fromAidev = isFromAidev(event);
  const isUserEvent = (type === 'user' || type === 'human') && !fromAidev;
  const rowBg = isUserEvent ? 'var(--color-brand-dim)' : index % 2 === 0 ? 'var(--color-surface)' : 'var(--color-surface-2)';

  return (
    <div
      className={`rounded px-4 ${isToolEvent ? 'py-2' : 'py-3'} ${isUserEvent ? 'border-r-3' : 'border-l-3'}`}
      style={{ backgroundColor: rowBg, [isUserEvent ? 'borderRightColor' : 'borderLeftColor']: fromAidev ? 'var(--color-surface-3)' : getEventBorderColor(event) }}
    >
      {isToolEvent ? (
        renderEvent(event, collapsed)
      ) : (
        <>
          <div className="mb-1 flex items-center gap-1.5 font-mono text-[10px] opacity-40" title={formatAbsoluteTime(event.timestamp)}>
            <span>{formatRelativeTime(event.timestamp)}</span>
            <span>#{event.id}</span>
            {fromAidev && <span>from aidev</span>}
          </div>
          {renderEvent(event, collapsed)}
        </>
      )}
    </div>
  );
}

function computeCollapsedIds(events: SSEEvent[]): Set<number> {
  const collapsed = new Set<number>();
  let hasLaterAssistant = false;
  for (let i = events.length - 1; i >= 0; i--) {
    const type = String(events[i].data.type ?? '');
    if (type === 'assistant' || type === 'text') {
      hasLaterAssistant = true;
    } else if (hasLaterAssistant && (type === 'tool_use' || type === 'tool_result')) {
      collapsed.add(events[i].id);
    }
  }
  return collapsed;
}

/** Frames during which a long list keeps going back to its last event while its rows are measured */
const SETTLE_FRAMES = 12;

function VirtualizedList({ events, containerRef, collapsedIds }: { events: SSEEvent[]; containerRef: React.RefObject<HTMLDivElement | null>; collapsedIds: Set<number> }) {
  const virtualizer = useVirtualizer({
    count: events.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 80,
    overscan: 10,
    getItemKey: (index) => events[index].id,
  });

  // A long session opens at its last event (#1224). Setting scrollTop is not enough here: the rows are estimated, then
  // measured, and the list starts at the top. The virtualizer is told to show the last event, again each frame while
  // the measures change the height, unless the operator scrolls first
  const opened = useRef(false);
  // An effect, not a layout effect: the container's ref (on the parent) is only set once this list's layout ran
  useEffect(() => {
    const el = containerRef.current;
    if (opened.current || !el || events.length === 0) return;
    opened.current = true;
    let frame = 0;
    let raf = 0;
    let stopped = false;
    const stop = () => { stopped = true; };
    // Any way he scrolls himself: the wheel, a finger, the scrollbar, the keyboard
    const kinds = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    for (const k of kinds) el.addEventListener(k, stop, { passive: true, once: true });
    const settle = () => {
      if (stopped) return;
      virtualizer.scrollToIndex(events.length - 1, { align: 'end' });
      if (++frame < SETTLE_FRAMES) raf = requestAnimationFrame(settle);
    };
    settle();
    return () => {
      cancelAnimationFrame(raf);
      for (const k of kinds) el.removeEventListener(k, stop);
    };
    // Once, when the session's events first arrive (the timeline is mounted again for each session, keyed by it): later
    // ones are followed by the timeline's own effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.length > 0]);

  return (
    <div style={{ height: virtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
      {virtualizer.getVirtualItems().map((virtualRow) => (
        <div
          key={virtualRow.key}
          data-index={virtualRow.index}
          ref={virtualizer.measureElement}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${virtualRow.start}px)` }}
        >
          <EventRow event={events[virtualRow.index]} index={virtualRow.index} collapsed={collapsedIds.has(events[virtualRow.index].id)} />
        </div>
      ))}
    </div>
  );
}

function PlainList({ events, collapsedIds }: { events: SSEEvent[]; collapsedIds: Set<number> }) {
  return (
    <div className="space-y-1">
      {events.map((event, i) => (
        <div key={event.id}>
          <EventRow event={event} index={i} collapsed={collapsedIds.has(event.id)} />
        </div>
      ))}
    </div>
  );
}

const reducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function EventTimeline({ events: rawEvents, isProcessing, onScrollChange, onScrolled }: EventTimelineProps) {
  const events = useMemo(() => markWaitingPrompts(hideEchoedPrompts(rawEvents)), [rawEvents]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const prevLengthRef = useRef(events.length);
  const useVirtual = events.length >= VIRTUALIZE_THRESHOLD;
  const collapsedIds = useMemo(() => computeCollapsedIds(events), [events]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    setAutoScroll(atBottom);
    onScrollChange?.(atBottom);
    onScrolled?.(el.scrollTop > 30);
  }, [onScrollChange, onScrolled]);

  // The session opens at its last event, at once: the first time there are events (from the cache or the history),
  // and again once a long list's rows are measured. Then it follows new events while the operator is at the bottom
  // One timeline per session (keyed by the page): `opened` is this session's
  const opened = useRef(false);
  const measured = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (opened.current || !el || events.length === 0) return;
    opened.current = true;
    el.scrollTop = el.scrollHeight;
    measured.current = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [events.length]);
  useEffect(() => () => {
    if (measured.current !== null) cancelAnimationFrame(measured.current);
  }, []);

  useEffect(() => {
    if (autoScroll && (events.length > prevLengthRef.current || isProcessing)) {
      const el = containerRef.current;
      if (el && typeof el.scrollTo === 'function') {
        el.scrollTo({ top: el.scrollHeight, behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    }
    prevLengthRef.current = events.length;
  }, [events.length, autoScroll, isProcessing]);

  const scrollToBottom = useCallback(() => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: reducedMotion() ? 'auto' : 'smooth',
    });
    setAutoScroll(true);
  }, []);

  if (events.length === 0) {
    return (
      <div className="flex-1">
        {isProcessing ? (
          <ProcessingIndicator />
        ) : (
          <div className="flex items-center justify-center py-12 text-sm" style={{ color: 'var(--color-text-3)' }}>
            No events yet
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative flex-1">
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="absolute inset-0 overflow-y-auto"
      >
        {useVirtual
          ? <VirtualizedList events={events} containerRef={containerRef} collapsedIds={collapsedIds} />
          : <PlainList events={events} collapsedIds={collapsedIds} />}
        {isProcessing && <ProcessingIndicator />}
      </div>

      {/* Scroll to bottom FAB */}
      {!autoScroll && (
        <>
          <style>{`
            @keyframes fab-slide-up {
              from { opacity: 0; transform: translateY(8px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium shadow-lg"
            style={{ backgroundColor: 'var(--color-surface-3)', color: 'var(--color-text)', animation: 'fab-slide-up 250ms ease-out' }}
            data-testid="scroll-to-bottom"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
            </svg>
            New messages below
          </button>
        </>
      )}
    </div>
  );
}
